import type { ContactType, ConversationStatus, Role } from "./types";

/** User-facing labels (Spanish). */
export const STATUS_LABEL: Record<ConversationStatus, string> = {
  bot: "IA",
  human: "Asesor",
  closed: "Cerrada",
};
export const ROLE_LABEL: Record<Role, string> = { owner: "Propietario", admin: "Administrador", agent: "Asesor" };
export const CONTACT_TYPE_LABEL: Record<ContactType, string> = {
  consumer: "Consumidor",
  business: "Comercio",
  institution: "Institución",
};

/** +506 8888 7777 style for Costa Rican numbers, +<digits> otherwise. */
/** Groups digits for the region's common numbers; anything else stays as +digits. */
const PHONE_FORMATS: { cc: string; length: number; groups: number[] }[] = [
  ...["502", "503", "504", "505", "506", "507"].map((cc) => ({ cc, length: 8, groups: [4, 4] })),
  { cc: "1", length: 10, groups: [3, 3, 4] },
  { cc: "521", length: 10, groups: [2, 4, 4] },
  { cc: "52", length: 10, groups: [2, 4, 4] },
  { cc: "57", length: 10, groups: [3, 3, 4] },
  { cc: "34", length: 9, groups: [3, 3, 3] },
];

export function formatPhone(waId: string) {
  for (const f of PHONE_FORMATS) {
    if (!waId.startsWith(f.cc) || waId.length !== f.cc.length + f.length) continue;
    let rest = waId.slice(f.cc.length);
    const parts = f.groups.map((n) => {
      const part = rest.slice(0, n);
      rest = rest.slice(n);
      return part;
    });
    const cc = f.cc === "521" ? "52 1" : f.cc;
    return `+${cc} ${parts.join(" ")}`;
  }
  return `+${waId}`;
}

export function initials(name: string | null, fallback: string) {
  const source = (name ?? "").trim();
  if (!source) return fallback.slice(-2);
  const parts = source.split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase();
}

const timeFmt = new Intl.DateTimeFormat("es-CR", { hour: "numeric", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat("es-CR", { day: "numeric", month: "short" });
const longDayFmt = new Intl.DateTimeFormat("es-CR", { weekday: "long", day: "numeric", month: "long" });

function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

/** Short time for lists: "3:42 p. m." today, "ayer", or "12 oct.". */
export function shortTime(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return timeFmt.format(d);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return "ayer";
  return dayFmt.format(d);
}

export function clock(iso: string) {
  return timeFmt.format(new Date(iso));
}

export function dayLabel(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return "Hoy";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return "Ayer";
  return longDayFmt.format(d);
}

/** Time left in the 24 h window, e.g. "quedan 3 h". */
export function windowLeft(lastInboundAt: string | null) {
  if (!lastInboundAt) return null;
  const ms = new Date(lastInboundAt).getTime() + 24 * 3600_000 - Date.now();
  if (ms <= 0) return null;
  const hours = Math.floor(ms / 3600_000);
  return hours >= 1 ? `quedan ${hours} h` : `quedan ${Math.max(1, Math.round(ms / 60_000))} min`;
}

export const TEMPLATE_CATEGORY_LABEL: Record<string, string> = {
  MARKETING: "Marketing",
  UTILITY: "Utilidad",
  AUTHENTICATION: "Autenticación",
};
export const TEMPLATE_STATUS_LABEL: Record<string, string> = {
  APPROVED: "Aprobada",
  PENDING: "En revisión",
  REJECTED: "Rechazada",
  PAUSED: "Pausada",
  DISABLED: "Desactivada",
};

/** Fills {{1}} / {{name}} placeholders for previews; empty ones stay visible. */
export function fillTemplate(text: string, values: Record<string, string>) {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => values[k]?.trim() || `{{${k}}}`);
}

/**
 * Why WhatsApp did not send a message, in the panel's words (Meta's error codes). 0 is
 * Orbis not reaching WhatsApp at all.
 */
const FAILURE_TEXT: Record<number, string> = {
  0: "No se pudo conectar con WhatsApp. Intenta de nuevo en un momento.",
  131030:
    "El número de prueba de WhatsApp solo puede escribir a números autorizados en Meta (hasta 5). Agrega este número en la app de Meta, o conecta un número real.",
  131047:
    "Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo deja escribirle con una plantilla aprobada.",
  131026:
    "WhatsApp no pudo entregarlo: el número no tiene WhatsApp, usa una versión vieja o bloqueó el número de la empresa.",
  131021: "No se puede enviar un mensaje al mismo número de la empresa.",
  131049: "WhatsApp no lo entregó para no saturar al cliente con mensajes de marketing. Intenta más adelante.",
  130472: "El cliente está en un grupo de prueba de Meta que no recibe mensajes de marketing.",
  131051: "WhatsApp no admite ese tipo de mensaje.",
  131052: "WhatsApp no pudo descargar el archivo del mensaje.",
  131053: "WhatsApp no pudo procesar el archivo adjunto (formato o tamaño).",
  131056: "Se enviaron demasiados mensajes seguidos a este cliente. Espera un momento antes de escribirle de nuevo.",
  130429: "Se alcanzó el límite de envíos de WhatsApp por segundo. Intenta de nuevo en unos minutos.",
  131048: "WhatsApp limitó los envíos de este número porque muchos clientes lo bloquearon o reportaron.",
  80007: "Se alcanzó el límite de envíos de la cuenta de WhatsApp. Intenta de nuevo más tarde.",
  131042: "Hay un problema con el método de pago de la cuenta de WhatsApp en Meta.",
  131031: "Meta bloqueó o restringió la cuenta de WhatsApp de la empresa.",
  368: "Meta restringió la cuenta por un tiempo por incumplir sus políticas.",
  190: "El acceso de Orbis a WhatsApp venció o no es válido: hay que conectar el número de nuevo.",
  133010: "El número de la empresa no está registrado en WhatsApp Business.",
  132000: "La plantilla no coincide con la aprobada en Meta: revisa cuántas variables lleva.",
  132001: "Esa plantilla no existe en ese idioma o todavía no está aprobada.",
  132005: "El texto de la plantilla con sus variables quedó demasiado largo.",
  132007: "La plantilla no cumple las políticas de Meta.",
  132012: "Las variables de la plantilla no tienen el formato que pide Meta.",
  132015: "La plantilla está pausada en Meta por baja calidad.",
  132016: "La plantilla fue desactivada en Meta.",
  131000: "WhatsApp tuvo un error interno. Intenta de nuevo.",
};

export function failureText(code: number) {
  return FAILURE_TEXT[code] ?? `WhatsApp no lo envió (código ${code}). Intenta de nuevo o revisa la cuenta en Meta.`;
}
